/**
 * Exibição do impacto ambiental (dashboard e relatórios). O CÁLCULO é todo do
 * backend (`Calculations.buildVirtualEnvironment`, fatores do MCTI): aqui só se
 * soma, formata e aponta para o artigo da metodologia na Ajuda — os três
 * pontos de exibição usam estas funções para não divergirem.
 */
import { format } from 'utils/format';

import { VirtualInvoice } from 'entities/VirtualInvoice';

type Environment = VirtualInvoice['virtualEnvironment'];

/** Deep-link do artigo "Impacto ambiental" da central de Ajuda. */
export const ENVIRONMENT_METHODOLOGY_PATH = '/ajuda?topico=impacto-ambiental';

/** CO₂ em kg → "850 kg" ou "1,25 t" (tonelada a partir de 1.000 kg). */
export const formatCo2 = (kg: number): string =>
  kg >= 1000 ? `${format.number(kg / 1000, 2)} t` : `${format.energy(kg)} kg`;

/** Fator em kg CO₂/kWh com 3 casas ("0,047"). */
export const formatFactor = (kgPerKwh: number): string => format.number(kgPerKwh, 3);

/** "2026-07" → "07/2026"; lista vira "07/2026 e 08/2026". */
export const formatFactorMonths = (months: string[] = []): string =>
  months
    .map((m) => `${m.slice(5, 7)}/${m.slice(0, 4)}`)
    .join(' e ');

export interface EnvironmentTotals {
  emissionKg: number;
  fallenTrees: number;
  petrolLiters: number;
  gridKwh: number;
  avoidedKg: number;
  plantedTrees: number;
  avoidedPetrolLiters: number;
  generationKwh: number;
  /** Fator médio efetivo do conjunto = CO₂ emitido ÷ kWh da rede. */
  averageFactor: number;
  /** Margem combinada efetiva = CO₂ evitado ÷ kWh gerado. */
  combinedMarginFactor: number;
  provisional: boolean;
  avoidedFromInjection: boolean;
  /** false = alguma fatura sem fator carregado (indicadores zerados). */
  factorsAvailable: boolean;
}

/** Soma os blocos de várias faturas (dashboard) — ou de uma só (relatório). */
export const sumEnvironment = (
  list: (Environment | undefined)[]
): EnvironmentTotals => {
  const t: EnvironmentTotals = {
    emissionKg: 0,
    fallenTrees: 0,
    petrolLiters: 0,
    gridKwh: 0,
    avoidedKg: 0,
    plantedTrees: 0,
    avoidedPetrolLiters: 0,
    generationKwh: 0,
    averageFactor: 0,
    combinedMarginFactor: 0,
    provisional: false,
    avoidedFromInjection: false,
    factorsAvailable: true,
  };
  list.forEach((e) => {
    if (!e) return;
    t.emissionKg += e.emissionCO2 || 0;
    t.fallenTrees += e.fallenTrees || 0;
    t.petrolLiters += e.petrolLiters || 0;
    t.gridKwh += e.gridConsumptionKwh || 0;
    t.avoidedKg += e.avoidedCO2Emission || 0;
    t.plantedTrees += e.plantedTrees || 0;
    t.avoidedPetrolLiters += e.avoidedPetrolLiters || 0;
    t.generationKwh += e.generationKwh || 0;
    t.provisional = t.provisional || !!e.factorProvisional;
    t.avoidedFromInjection = t.avoidedFromInjection || !!e.avoidedFromInjection;
    if (e.factorsAvailable === false) t.factorsAvailable = false;
  });
  t.averageFactor = t.gridKwh > 0 ? t.emissionKg / t.gridKwh : 0;
  t.combinedMarginFactor =
    t.generationKwh > 0 ? t.avoidedKg / t.generationKwh : 0;
  return t;
};
